import React, { useRef, useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { Download, FileText, Image as ImageIcon, Sparkles } from 'lucide-react';
import { useInviteTheme } from '../pages/Invite/InviteThemeContext';

interface InviteCardGeneratorProps {
  eventName: string;
  eventType: string;
  eventDate: string;
  eventVenue: string;
  eventCity: string;
  rsvpLink: string;
  theme?: string;
}

export const InviteCardGenerator: React.FC<InviteCardGeneratorProps> = ({
  eventName,
  eventType,
  eventDate,
  eventVenue,
  eventCity,
  rsvpLink,
  theme = 'warm'
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);
  const { theme: appTheme } = useInviteTheme();
  const isLightTheme = appTheme === 'light';

  // Preload Shadigram Logo (use white logo for dark gradient backgrounds)
  useEffect(() => {
    const img = new Image();
    img.src = '/shadigram-logo-white.png';
    img.onload = () => {
      setLogoImage(img);
      setIsLogoLoaded(true);
    };
    img.onerror = () => {
      console.warn('Failed to load white Shadigram logo, attempting dark/fallback logo');
      const fallbackImg = new Image();
      fallbackImg.src = '/shadigram-logo.png';
      fallbackImg.onload = () => {
        setLogoImage(fallbackImg);
        setIsLogoLoaded(true);
      };
      fallbackImg.onerror = () => {
        setIsLogoLoaded(true);
      };
    };
  }, []);

  // Format Date for Invitation Card
  const formatEventDate = (dateStr: string) => {
    if (!dateStr) return 'Date to be announced';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      
      const options: Intl.DateTimeFormatOptions = { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      const formattedDate = d.toLocaleDateString('en-US', options);
      
      const hours = d.getHours();
      const minutes = d.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedTime = `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      
      return `${formattedDate} at ${formattedTime}`;
    } catch (e) {
      return dateStr;
    }
  };

  // --- CANVAS DECORATION HELPERS ---

  // Golden Mandala Background (for marriage and anniversary)
  const drawMandala = (ctx: CanvasRenderingContext2D, cx: number, cy: number, maxRadius: number, color: string) => {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.07; // subtle watermark

    for (let r = 40; r <= maxRadius; r += 40) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    const petals = 24;
    for (let i = 0; i < petals; i++) {
      const angle = (i * Math.PI * 2) / petals;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + maxRadius * cos, cy + maxRadius * sin);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(cx + (maxRadius - 40) * cos, cy + (maxRadius - 40) * sin, 25, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  };

  // Traditional Marigold Garland (for marriage and housewarming)
  const drawGarland = (ctx: CanvasRenderingContext2D, width: number, y: number, color1: string, color2: string) => {
    ctx.save();
    const segments = 10;
    const segmentWidth = (width - 80) / segments;
    
    for (let i = 0; i < segments; i++) {
      const startX = 40 + i * segmentWidth;
      const endX = startX + segmentWidth;
      const centerY = y + 5;
      
      // Draw looping garland curves
      const dotsCount = 7;
      for (let j = 0; j <= dotsCount; j++) {
        const t = j / dotsCount;
        const x = startX + t * segmentWidth;
        const yPos = centerY + 28 * 4 * t * (1 - t); // parabolic loop path
        
        // Draw flower circle
        ctx.fillStyle = j % 2 === 0 ? color1 : color2;
        ctx.beginPath();
        ctx.arc(x, yPos, 9, 0, Math.PI * 2);
        ctx.fill();
        
        // White-gold inner petal highlight
        ctx.fillStyle = '#fff7ed';
        ctx.beginPath();
        ctx.arc(x, yPos, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  };

  // Floating Confetti Sparkles (for birthdays)
  const drawConfetti = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.save();
    const colors = ['#f472b6', '#38bdf8', '#fbbf24', '#c084fc', '#f87171', '#4ade80'];
    for (let i = 0; i < 70; i++) {
      const x = Math.random() * (width - 100) + 50;
      const y = Math.random() * (height - 300) + 50;
      const size = Math.random() * 8 + 4;
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
      ctx.globalAlpha = Math.random() * 0.35 + 0.15;

      ctx.beginPath();
      if (Math.random() > 0.4) {
        ctx.fillRect(x, y, size, size * (Math.random() > 0.5 ? 1.5 : 1));
      } else {
        // Draw small sparkle star
        ctx.arc(x, y, size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  };

  // Elegant Leaves and Hanging Vines (for housewarming, anniversaries and others)
  const drawLeafVines = (ctx: CanvasRenderingContext2D, width: number, height: number, color: string) => {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.12;
    ctx.lineWidth = 2.5;

    const drawSingleVine = (startX: number, startY: number, length: number, direction: 1 | -1) => {
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.bezierCurveTo(
        startX + 50 * direction, startY + length / 3,
        startX - 40 * direction, startY + (2 * length) / 3,
        startX, startY + length
      );
      ctx.stroke();

      for (let yPos = startY + 30; yPos < startY + length - 20; yPos += 45) {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(startX + 12 * direction, yPos, 10, 5, (Math.PI / 4) * direction, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    drawSingleVine(55, 55, 450, 1);
    drawSingleVine(width - 55, 55, 450, -1);
    ctx.restore();
  };

  // Draw Card Procedure
  const drawCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 1. CHOOSE OCCASION PALETTE
    let primaryColor = '#0f172a'; // slate-900
    let secondaryColor = '#1e293b'; // slate-800
    let accentColor = '#D4AF37'; // Royal Gold

    if (eventType === 'marriage' || eventType === 'wedding') {
      primaryColor = '#4c0519'; // rose-950 (maroon)
      secondaryColor = '#881337'; // rose-900 (crimson)
      accentColor = '#F59E0B'; // Amber Gold
    } else if (eventType === 'birthday') {
      primaryColor = '#1e1b4b'; // indigo-950
      secondaryColor = '#312e81'; // indigo-900
      accentColor = '#f472b6'; // Rose Pink
    } else if (eventType === 'housewarming') {
      primaryColor = '#064e3b'; // emerald-950
      secondaryColor = '#065f46'; // emerald-900
      accentColor = '#f59e0b'; // Amber Gold
    } else if (eventType === 'anniversary') {
      primaryColor = '#311042'; // deep violet
      secondaryColor = '#4a1d6d'; // purple
      accentColor = '#F472B6'; // Rose Gold / Pink
    }

    // Radial gradient background
    const bgGradient = ctx.createRadialGradient(
      canvas.width / 2, canvas.height / 2, 80,
      canvas.width / 2, canvas.height / 2, canvas.width
    );
    bgGradient.addColorStop(0, secondaryColor);
    bgGradient.addColorStop(1, primaryColor);
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 2. INNER CORNER BORDERS & ACCENTS
    ctx.lineWidth = 4;
    ctx.strokeStyle = accentColor;
    ctx.strokeRect(35, 35, canvas.width - 70, canvas.height - 70);

    ctx.lineWidth = 1;
    ctx.strokeStyle = accentColor + '66';
    ctx.strokeRect(45, 45, canvas.width - 90, canvas.height - 90);

    // Corner decorations
    const drawCornerDecor = (x: number, y: number, xFactor: number, yFactor: number) => {
      ctx.beginPath();
      ctx.moveTo(x + 30 * xFactor, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y + 30 * yFactor);
      ctx.lineWidth = 6;
      ctx.strokeStyle = accentColor;
      ctx.stroke();
    };
    drawCornerDecor(35, 35, 1, 1);
    drawCornerDecor(canvas.width - 35, 35, -1, 1);
    drawCornerDecor(35, canvas.height - 35, 1, -1);
    drawCornerDecor(canvas.width - 35, canvas.height - 35, -1, -1);

    // 3. DRAW OCCASION SPECIFIC ILLUSTRATIONS
    if (eventType === 'marriage' || eventType === 'wedding') {
      drawMandala(ctx, canvas.width / 2, 330, 240, accentColor);
      drawGarland(ctx, canvas.width, 45, '#ea580c', '#eab308'); // Orange & Yellow Marigolds
    } else if (eventType === 'birthday') {
      drawConfetti(ctx, canvas.width, canvas.height);
    } else if (eventType === 'housewarming') {
      drawLeafVines(ctx, canvas.width, canvas.height, accentColor);
      drawGarland(ctx, canvas.width, 45, '#ea580c', '#eab308'); // Traditional toran garland
    } else if (eventType === 'anniversary') {
      drawMandala(ctx, canvas.width / 2, 330, 220, accentColor);
      drawLeafVines(ctx, canvas.width, canvas.height, '#f472b6');
    } else {
      drawLeafVines(ctx, canvas.width, canvas.height, accentColor);
    }

    // 4. HEADER TEXT
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = accentColor;
    ctx.font = 'bold 22px "Outfit", sans-serif';
    
    let headerText = 'YOU ARE CORDIALLY INVITED TO CELEBRATE WITH US';
    if (eventType === 'birthday') headerText = 'JOIN US FOR THE BIRTHDAY CELEBRATION';
    if (eventType === 'housewarming') headerText = 'JOIN US FOR THE GRAPRAVESH & CELEBRATION';
    if (eventType === 'anniversary') headerText = 'CELEBRATING YEARS OF TOGETHERNESS & LOVE';
    ctx.fillText(headerText, canvas.width / 2, 120);

    // Separator line
    ctx.beginPath();
    ctx.moveTo(canvas.width / 2 - 120, 150);
    ctx.lineTo(canvas.width / 2 + 120, 150);
    ctx.strokeStyle = accentColor + '77';
    ctx.lineWidth = 2;
    ctx.stroke();

    // 5. EVENT TITLE WRAPPER
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 52px "Outfit", sans-serif';
    const displayTitle = eventName || 'Our Celebration';
    const words = displayTitle.split(' ');
    let line = '';
    const lines = [];
    const maxWidth = canvas.width - 160;
    const lineHeight = 65;

    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && n > 0) {
        lines.push(line);
        line = words[n] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line);

    let startY = 220;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i].trim(), canvas.width / 2, startY);
      startY += lineHeight;
    }

    // 6. EVENT SUMMARY BOX (FROSTED PANEL)
    const cardY = startY + 25;
    const cardHeight = 240;
    const cardWidth = canvas.width - 160;
    const cardX = 80;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.fillRect(cardX, cardY, cardWidth, cardHeight);
    ctx.strokeStyle = accentColor + '55';
    ctx.lineWidth = 2;
    ctx.strokeRect(cardX, cardY, cardWidth, cardHeight);

    // Box details
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px "Outfit", sans-serif';
    ctx.fillText('📅 WHEN', canvas.width / 2, cardY + 45);
    
    ctx.fillStyle = '#f1f5f9';
    ctx.font = '400 23px "Outfit", sans-serif';
    ctx.fillText(formatEventDate(eventDate), canvas.width / 2, cardY + 80);

    ctx.fillStyle = accentColor;
    ctx.font = 'bold 24px "Outfit", sans-serif';
    ctx.fillText('📍 WHERE', canvas.width / 2, cardY + 145);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px "Outfit", sans-serif';
    ctx.fillText(eventVenue || 'Venue to be announced', canvas.width / 2, cardY + 180);
    
    ctx.fillStyle = '#cbd5e1';
    ctx.font = '500 20px "Outfit", sans-serif';
    ctx.fillText(eventCity || '', canvas.width / 2, cardY + 210);

    // 7. QR CODE & RSVP CONTAINER
    const rsvpY = cardY + cardHeight + 40;
    const qrCanvas = document.getElementById('canvas-card-generator-qr') as HTMLCanvasElement;
    
    if (qrCanvas) {
      const qrSize = 170;
      const qrX = canvas.width / 2 - qrSize / 2;
      const qrY = rsvpY;

      // Draw clean canvas card background wrapper
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 3;
      ctx.strokeRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);

      // Compositing
      ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 18px "Outfit", sans-serif';
      ctx.fillText('SCAN QR CODE TO RSVP ONLINE', canvas.width / 2, qrY + qrSize + 30);
    }

    // RSVP URL
    ctx.fillStyle = accentColor;
    ctx.font = 'bold 18px "Outfit", sans-serif';
    ctx.fillText(rsvpLink ? rsvpLink.replace(/^https?:\/\//, '') : 'shadigram.com/invite', canvas.width / 2, rsvpY + 235);

    // 8. SHADIGRAM BRAND LOGO & TAGLINE (Centered side-by-side layout inside poster boundaries)
    const footerY = canvas.height - 100; // Keep it safely above the double gold borders
    
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    
    const spacing = 12;
    const tagText = '|  powered by : shadigram.com';
    ctx.font = '500 13px "Outfit", sans-serif';
    const tagTextWidth = ctx.measureText(tagText).width;
    
    if (logoImage && isLogoLoaded && logoImage.width) {
      const logoWidth = 65;
      const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
      const totalWidth = logoWidth + spacing + tagTextWidth;
      const startX = (canvas.width - totalWidth) / 2; // Center-justify the combined block
      
      ctx.drawImage(logoImage, startX, footerY - logoHeight / 2, logoWidth, logoHeight);
      
      ctx.fillStyle = '#94a3b8';
      ctx.fillText(tagText, startX + logoWidth + spacing, footerY);
    } else {
      const fallbackText = '✉️ Shadigram';
      ctx.font = 'bold 16px "Outfit", sans-serif';
      const fallbackWidth = ctx.measureText(fallbackText).width;
      
      const totalWidth = fallbackWidth + spacing + tagTextWidth;
      const startX = (canvas.width - totalWidth) / 2;
      
      ctx.fillStyle = '#ffffff';
      ctx.fillText(fallbackText, startX, footerY);
      
      ctx.fillStyle = '#94a3b8';
      ctx.font = '500 13px "Outfit", sans-serif';
      ctx.fillText(tagText, startX + fallbackWidth + spacing, footerY);
    }
  };

  useEffect(() => {
    if (isLogoLoaded) {
      const timer = setTimeout(() => {
        drawCard();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [eventName, eventType, eventDate, eventVenue, eventCity, rsvpLink, isLogoLoaded, theme]);

  const handleDownloadPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `${eventName.replace(/[^a-z0-9]/gi, '_')}_Invitation_Card.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleDownloadPDF = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const imgData = canvas.toDataURL('image/png');
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const cardWidth = 160;
    const cardHeight = (canvas.height / canvas.width) * cardWidth;
    const xOffset = (pdfWidth - cardWidth) / 2;
    const yOffset = (pdfHeight - cardHeight) / 2;

    pdf.setFillColor(15, 23, 42); // slate-900 backdrop matching card primary background
    pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
    pdf.addImage(imgData, 'PNG', xOffset, yOffset, cardWidth, cardHeight);
    
    pdf.save(`${eventName.replace(/[^a-z0-9]/gi, '_')}_Invitation_Poster.pdf`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {/* Hidden QR Code canvas source */}
      <div style={{ display: 'none' }}>
        <QRCodeCanvas 
          id="canvas-card-generator-qr" 
          value={rsvpLink || 'https://shadigram.com/invite'} 
          size={250} 
          level="H" 
          includeMargin={true}
        />
      </div>

      {/* Scaled Preview Frame */}
      <div style={{
        boxShadow: isLightTheme ? '0 10px 30px rgba(0,0,0,0.06)' : '0 16px 40px rgba(0,0,0,0.45)',
        borderRadius: '16px',
        overflow: 'hidden',
        border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(139, 92, 246, 0.35)',
        width: '100%',
        maxWidth: '380px',
        marginBottom: '1.5rem',
        background: isLightTheme ? '#FFFFFF' : '#0f172a'
      }}>
        <canvas 
          ref={canvasRef} 
          width={800} 
          height={1200}
          style={{
            display: 'block',
            width: '100%',
            height: 'auto'
          }}
        />
      </div>

      {/* Export Action Controls */}
      <div style={{ display: 'flex', gap: '1rem', width: '100%', maxWidth: '380px', justifyContent: 'center' }}>
        <button
          onClick={handleDownloadPNG}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
            color: 'white',
            border: 'none',
            padding: '11px 16px',
            borderRadius: '12px',
            fontSize: '0.88rem',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
            transition: 'all 0.2s'
          }}
          title="Download mobile-optimized invitation card image"
        >
          <ImageIcon size={16} />
          Save PNG (WhatsApp)
        </button>
        
        <button
          onClick={handleDownloadPDF}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            backgroundColor: isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.05)',
            color: isLightTheme ? '#334155' : '#CBD5E1',
            border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)',
            padding: '11px 16px',
            borderRadius: '12px',
            fontSize: '0.88rem',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: isLightTheme ? '0 2px 6px rgba(0,0,0,0.03)' : 'none',
            transition: 'all 0.2s'
          }}
          title="Download high-resolution print poster"
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = isLightTheme ? '#F8FAFC' : 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.borderColor = isLightTheme ? '#94A3B8' : 'rgba(139, 92, 246, 0.4)';
            if (isLightTheme) e.currentTarget.style.color = '#0F172A';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.borderColor = isLightTheme ? '#CBD5E1' : 'rgba(255, 255, 255, 0.12)';
            if (isLightTheme) e.currentTarget.style.color = '#334155';
          }}
        >
          <FileText size={16} />
          Save PDF (Print)
        </button>
      </div>
    </div>
  );
};
